import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { EnderecoDto } from "@/dtos/EnderedoDto";
import CompraRapidaService, {
    BuscarClientePorCPFDTO,
    CadastrarUsuarioCompraRapidaDto,
} from "@/services/CompraRapidaService";
import EnderecoService from "@/services/EnderecoService";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { EtapaSelecionarQuantidadeGiro } from "./EtapaSelecionarQuantidade";
import { EtapaInserirCPF } from "../CompraRapida/EtapaInserirCPF";
import { Button } from "@/components/ui/button";
import { EtapaCadastrar } from "../CompraRapida/EtapaCadastrar";
import { EtapaResumo } from "../CompraRapida/EtapaResumo";
import { useAuth } from "@/contexts/AuthProvider";
import { ScrollArea } from "@/components/ui/scroll-area";
import SorteioService from "@/services/SorteioService";
import { PacotePromocionalDto, SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import CidadesService, { CidadeDto } from "@/services/CidadesService";
import StorageUtils from "@/utils/StorageUtils";
import { Separator } from "@/components/ui/separator";
import CompraRapidaOqueE from "@/components/CompraRapidaOqueE";
import { normalizarCPF, validarCPF } from "@/utils/Cpf";
import { validarNomeCompleto } from "@/utils/NomeCompleto";
import { SelecaoCompraGiro } from "@/components/CompraRapidaGiro";
import { CheckoutCompraRapidaSorteioGiroDto } from "@/dtos/Request/CheckoutCompraRapidaSorteioGiroDto";
import { labelPlural } from "@/utils/copyTituloBilhete";
import {
    criarSelecaoPacote,
    criarSelecaoQuantidade,
} from "@/utils/selecaoPacoteGiro";
import { EtapasCompraRapida } from "../CompraRapida";

export enum EtapasCompraRapidaGiro {
    SelecionarQuantidade = 0,
    InserirCPF = 1,
    CadastrarDados = 2,
    Resumo = 3,
}

interface LocationState {
    selecao?: SelecaoCompraGiro;
}

function selecaoInicial(sorteioGiro: SorteioGiroDto): {
    selecao: SelecaoCompraGiro;
    quantidade: number;
    pacote: PacotePromocionalDto | null;
} {
    const qtdMinima = sorteioGiro.QtdMinima || 1;
    return {
        selecao: criarSelecaoQuantidade(qtdMinima, sorteioGiro.Valor || 0),
        quantidade: qtdMinima,
        pacote: null,
    };
}

function resolverSelecaoDeepLink(
    sorteioGiro: SorteioGiroDto,
    modo: string | null,
    qtdParam: string | null,
    idPacoteParam: string | null
): { selecao: SelecaoCompraGiro; quantidade: number; pacote: PacotePromocionalDto | null } | null {
    const pacotes = sorteioGiro.PacotesPromocionais ?? [];

    if (modo === "pacote" && idPacoteParam) {
        const pacote = pacotes.find(
            (p) => p.IdPacotePromocional === parseInt(idPacoteParam, 10)
        );
        if (pacote) {
            return {
                selecao: criarSelecaoPacote(pacote),
                quantidade: pacote.Qtd,
                pacote,
            };
        }
    }

    if (modo === "quantidade" && qtdParam) {
        const qtd = parseInt(qtdParam, 10);
        if (qtd > 0) {
            return {
                selecao: criarSelecaoQuantidade(qtd, sorteioGiro.Valor || 0),
                quantidade: qtd,
                pacote: null,
            };
        }
    }

    return null;
}

export function CompraRapidaGiroPage() {
    const location = useLocation();
    const locationState = (location.state ?? {}) as LocationState;
    const [sorteioGiro, setSorteioGiro] = useState<SorteioGiroDto | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [selecao, setSelecao] = useState<SelecaoCompraGiro | null>(null);
    const [quantidade, setQuantidade] = useState(1);
    const [pacoteSelecionado, setPacoteSelecionado] = useState<PacotePromocionalDto | null>(
        null
    );
    const [quantidadeAvulsaAnterior, setQuantidadeAvulsaAnterior] = useState(1);
    const [etapa, setEtapa] = useState<EtapasCompraRapidaGiro>(
        EtapasCompraRapidaGiro.SelecionarQuantidade
    );
    const [submiting, setSubmiting] = useState(false);
    const [endereco, setEndereco] = useState<EnderecoDto>();
    const [usuario, setUsuario] = useState<BuscarClientePorCPFDTO>();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authProps = useAuth();
    const [cidades, setCidades] = useState<CidadeDto[]>([]);
    const [deepLinkAplicado, setDeepLinkAplicado] = useState(false);

    const formik = useFormik({
        initialValues: {
            CPF: "",
            Nome: "",
            Telefone: "",
            Email: "",
            CEP: "",
            Cidade: "",
            UF: "",
        },
        validationSchema: Yup.object({
            Nome: Yup.string()
                .required("Obrigatório")
                .test("Validar Nome Completo", "Informe o nome completo", validarNomeCompleto),
            CPF: Yup.string()
                .required("Obrigatório")
                .test("Validar CPF", "CPF Inválido", (cpfToValidate) =>
                    validarCPF(cpfToValidate || "")
                ),
            Telefone: Yup.string()
                .required("Obrigatório")
                .test("Validar Telefone", "Telefone Inválido", (telefoneToValidate) => {
                    if (
                        (telefoneToValidate || "")
                            .replace(/_/g, "")
                            .replace(/\(/g, "")
                            .replace(/\)/g, "")
                            .replace(/-/g, "")
                            .replace(/\./g, "")
                            .replace(/\s/g, "").length !== 11
                    ) {
                        return false;
                    }
                    return true;
                }),
            Email: Yup.string().email("Digite um email Válido").required("Obrigatório"),
        }),
        onSubmit: () => {},
    });

    useEffect(() => {
        async function buscarSorteioVigente() {
            try {
                const result = await SorteioService.buscarSorteioVigente();
                const giro = result.Data.SorteioGiro;
                if (!giro) {
                    navigate("/");
                    return;
                }
                setSorteioGiro(giro);
                const base = selecaoInicial(giro);
                setSelecao(base.selecao);
                setQuantidade(base.quantidade);
                setQuantidadeAvulsaAnterior(base.quantidade);
                setPacoteSelecionado(base.pacote);
            } catch (error: unknown) {
                toast({
                    variant: "destructive",
                    description: String(error),
                    duration: 4000,
                });
                navigate("/");
            } finally {
                setCarregando(false);
            }
        }

        buscarSorteioVigente();
    }, []);

    useEffect(() => {
        if (!sorteioGiro || deepLinkAplicado) {
            return;
        }

        const modo = searchParams.get("modo");
        const qtd = searchParams.get("qtd");
        const idPacote = searchParams.get("id");

        if (locationState.selecao) {
            const s = locationState.selecao;
            setSelecao(s);
            setQuantidade(s.quantidade);
            if (s.modo === "pacote") {
                const pacote = sorteioGiro.PacotesPromocionais?.find(
                    (p) => p.IdPacotePromocional === s.idPacotePromocional
                );
                setPacoteSelecionado(pacote ?? null);
            } else {
                setPacoteSelecionado(null);
            }
            setEtapa(EtapasCompraRapidaGiro.InserirCPF);
            if (authProps.isAuthorized) {
                formik.setFieldValue("CPF", authProps.usuario?.CPF);
            }
            setDeepLinkAplicado(true);
            return;
        }

        const deepLink = resolverSelecaoDeepLink(sorteioGiro, modo, qtd, idPacote);
        if (deepLink) {
            setSelecao(deepLink.selecao);
            setQuantidade(deepLink.quantidade);
            setPacoteSelecionado(deepLink.pacote);
            setEtapa(EtapasCompraRapidaGiro.InserirCPF);
            if (authProps.isAuthorized) {
                formik.setFieldValue("CPF", authProps.usuario?.CPF);
            }
        }

        setDeepLinkAplicado(true);
    }, [sorteioGiro, deepLinkAplicado]);

    useEffect(() => {
        const idVendedor = searchParams.get("idVendedor");
        if (idVendedor) {
            StorageUtils.setIdVendedor(idVendedor);
        }
    }, []);

    useEffect(() => {
        CidadesService.buscarCidades().then(setCidades);
    }, []);

    useEffect(() => {
        const cidade = cidades.find((c) => c.cidade == formik.values.Cidade);
        if (!cidade) {
            return;
        }
        setEndereco({ Cidade: formik.values.Cidade, CEP: "", UF: cidade.estado, Bairro: "" });
    }, [formik.values.Cidade, cidades]);

    useEffect(() => {
        const cep = formik.values.CEP.replace(/_/g, "").replace(/-/g, "");
        if (cep.length !== 8) {
            setEndereco(undefined);
            return;
        }

        EnderecoService.buscarPorCep(cep)
            .then(({ Data: result }) => {
                setEndereco(result);
                toast({
                    variant: "success",
                    description: "CEP Válido para compra",
                    duration: 4000,
                });
            })
            .catch((error: unknown) => {
                toast({
                    variant: "destructive",
                    description: String(error),
                    duration: 4000,
                });
            });
    }, [formik.values.CEP]);

    async function verificarRecaptcha() {
        try {
            const grecaptcha = (
                window as Window & {
                    grecaptcha?: {
                        execute: (key: string, opts: { action: string }) => Promise<string>;
                    };
                }
            ).grecaptcha;
            return await grecaptcha!.execute("6LdL-BcsAAAAAMp9u6pw0p90qR518KgqzfiSihU3", {
                action: "submit",
            });
        } catch (error: unknown) {
            toast({
                variant: "destructive",
                description: String(error),
                duration: 4000,
            });
            return null;
        }
    }

    function montarPayloadCheckout(
        usuarioCadastro?: CadastrarUsuarioCompraRapidaDto
    ): CheckoutCompraRapidaSorteioGiroDto | null {
        if (!selecao) {
            return null;
        }

        const base: CheckoutCompraRapidaSorteioGiroDto = {
            CPF: normalizarCPF(formik.values.CPF),
            IdVendedor: StorageUtils.getIdVendedor(),
            RecaptchaToken: "",
            Usuario: usuarioCadastro,
        };

        if (selecao.modo === "pacote") {
            return {
                ...base,
                QtdTitulo: 0,
                IdPacotePromocional: selecao.idPacotePromocional,
                QtdPacotePromocional: 1,
            };
        }

        return {
            ...base,
            QtdTitulo: selecao.quantidade,
        };
    }

    async function gerarQrCodeCompraRapidaGiro(usuarioCadastro?: CadastrarUsuarioCompraRapidaDto) {
        if (!selecao || sorteioGiro?.VendaBloqueada) {
            return;
        }

        try {
            setSubmiting(true);
            const cpf = normalizarCPF(formik.values.CPF);
            if (cpf.length !== 11 || !validarCPF(cpf)) {
                toast({
                    variant: "destructive",
                    description: "CPF Inválido",
                    duration: 4000,
                });
                return;
            }

            const token = await verificarRecaptcha();
            if (!token) {
                return;
            }

            const payload = montarPayloadCheckout(usuarioCadastro);
            if (!payload) {
                return;
            }
            payload.RecaptchaToken = token;

            const { Data: qrCodeGerado } =
                await CompraRapidaService.checkoutCompraRapidaSorteioGiro(payload);

            await toast({
                variant: "success",
                description: "QRCode gerado com sucesso",
                duration: 4000,
            });

            StorageUtils.removeIdVendedor();

            navigate("/compra/rapida/giro/qrcode", {
                state: {
                    qrCode: qrCodeGerado,
                    token: qrCodeGerado.TokenCompraRapida,
                },
            });
            navigate(0);
        } catch (error: unknown) {
            if (error === "CPF não encontrado;") {
                setUsuario(undefined);
                formik.setValues({ ...formik.values, Nome: "", Email: "", Telefone: "" });
                setEtapa(EtapasCompraRapidaGiro.CadastrarDados);
                return;
            }
            toast({
                variant: "destructive",
                description: String(error),
                duration: 4000,
            });
        } finally {
            setSubmiting(false);
        }
    }

    async function handleCadastrarEFinalizarCompra() {
        const novoUsuario = {
            CPF: normalizarCPF(formik.values.CPF),
            Email: formik.values.Email,
            Telefone: formik.values.Telefone.replace(/_/g, "")
                .replace(/\(/g, "")
                .replace(/\)/g, "")
                .replace(/-/g, "")
                .replace(/\./g, "")
                .replace(/\s/g, ""),
            Nome: formik.values.Nome,
            Endereco: {
                CEP: endereco?.CEP.replace(/_/g, "").replace(/-/g, ""),
                Cidade: endereco?.Cidade,
                Estado: "",
                UF: endereco?.UF,
                Numero: "",
                Bairro: "",
                Logradouro: "",
            },
            Senha: "",
        } as CadastrarUsuarioCompraRapidaDto;

        gerarQrCodeCompraRapidaGiro(novoUsuario);
    }

    function handleSelecaoChange(
        novaSelecao: SelecaoCompraGiro,
        novaQuantidade: number,
        pacote: PacotePromocionalDto | null
    ) {
        if (pacote && !pacoteSelecionado) {
            setQuantidadeAvulsaAnterior(quantidade);
        }
        setSelecao(novaSelecao);
        setQuantidade(novaQuantidade);
        setPacoteSelecionado(pacote);
    }

    function voltarParaSelecao() {
        setEtapa(EtapasCompraRapidaGiro.SelecionarQuantidade);
        setUsuario(undefined);
        setEndereco(undefined);
        formik.resetForm();
    }

    if (carregando || !sorteioGiro || !selecao) {
        return <>Carregando...</>;
    }

    return (
        <>
            <h1 className="text-3xl font-semibold text-center text-primary mb-5 mt-1">
                COMPRA RÁPIDA GIRO
            </h1>

            <ScrollArea>
                <Card className="mx-auto max-w-sm py-5 mb-5">
                    <CardContent>
                        <div className="flex justify-end mb-2">
                            <CompraRapidaOqueE />
                        </div>

                        {etapa === EtapasCompraRapidaGiro.SelecionarQuantidade && (
                            <EtapaSelecionarQuantidadeGiro
                                sorteioGiro={sorteioGiro}
                                selecao={selecao}
                                quantidade={quantidade}
                                quantidadeAvulsaAnterior={quantidadeAvulsaAnterior}
                                pacoteSelecionado={pacoteSelecionado}
                                alterarEtapa={setEtapa}
                                onSelecaoChange={handleSelecaoChange}
                                vendaBloqueada={sorteioGiro.VendaBloqueada}
                            />
                        )}

                        {etapa !== EtapasCompraRapidaGiro.SelecionarQuantidade && (
                            <>
                                <div className="flex justify-center flex-col items-center">
                                    <h1 className="text-center text-primary font-semibold">
                                        Seleção
                                    </h1>
                                    <p className="text-center text-primary text-2xl">
                                        {selecao.modo === "pacote"
                                            ? `${quantidade} ${labelPlural()}`
                                            : quantidade}
                                    </p>
                                    <p className="text-center text-primary text-lg">
                                        {selecao.valor.toLocaleString("pt-br", {
                                            style: "currency",
                                            currency: "BRL",
                                        })}
                                    </p>
                                </div>
                                <Separator />
                            </>
                        )}

                        {(etapa === EtapasCompraRapidaGiro.InserirCPF ||
                            etapa === EtapasCompraRapidaGiro.CadastrarDados) && (
                            <div className="flex flex-col">
                                <EtapaInserirCPF
                                    etapa={EtapasCompraRapida.InserirCPF}
                                    formik={formik}
                                    submiting={submiting}
                                />

                                {etapa === EtapasCompraRapidaGiro.CadastrarDados && (
                                    <EtapaCadastrar
                                        cidades={cidades}
                                        formik={formik}
                                        submiting={submiting}
                                    />
                                )}

                                <div className="flex justify-center gap-2 mt-5">
                                    <Button
                                        disabled={submiting}
                                        type="button"
                                        onClick={voltarParaSelecao}
                                        variant="foreground"
                                        className="mt-2"
                                    >
                                        Voltar
                                    </Button>

                                    {etapa === EtapasCompraRapidaGiro.CadastrarDados && (
                                        <Button
                                            disabled={
                                                !formik.isValid || !endereco || submiting
                                            }
                                            type="button"
                                            onClick={handleCadastrarEFinalizarCompra}
                                            variant="foreground"
                                            className="mt-2"
                                        >
                                            Continuar
                                        </Button>
                                    )}

                                    {etapa === EtapasCompraRapidaGiro.InserirCPF && (
                                        <Button
                                            disabled={
                                                submiting ||
                                                sorteioGiro.VendaBloqueada ||
                                                !validarCPF(formik.values.CPF)
                                            }
                                            type="button"
                                            onClick={() => gerarQrCodeCompraRapidaGiro()}
                                            variant="foreground"
                                            className="mt-2"
                                        >
                                            Gerar QRCode
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {usuario && etapa === EtapasCompraRapidaGiro.Resumo && (
                            <EtapaResumo
                                finalizandoCompra={submiting}
                                quantidade={quantidade}
                                usuario={usuario}
                                alterarUsuario={setUsuario}
                                formik={formik}
                                alterarEtapa={(e) => {
                                    if (e === EtapasCompraRapida.SelecionarQuantidade) {
                                        voltarParaSelecao();
                                    }
                                }}
                                handleLoginEFinalizarCompra={() =>
                                    gerarQrCodeCompraRapidaGiro()
                                }
                            />
                        )}
                    </CardContent>
                </Card>

                <div className="mb-5 flex flex-col justify-center items-center">
                    <div className="my-3 text-primary-foreground text-center">
                        <span>
                            Deseja visualizar suas compras?
                            <br />
                            <NavLink to="/AutenticarPorCodigo">
                                <Button type="button" className="gap-2">
                                    Clique Aqui!
                                </Button>
                            </NavLink>
                        </span>
                    </div>
                    <NavLink to="/compra/titulos">
                        <img
                            className="rounded-lg"
                            src={sorteioGiro.ImagemPremio}
                            alt="Prêmio Sorteio Giro"
                        />
                    </NavLink>
                </div>
            </ScrollArea>
        </>
    );
}
